// File: src/components/Common/MultiLocationSelector.tsx

import React, { useState, useRef, useEffect } from "react";

interface Location {
  id: number;
  label: string;
}

interface LocationMultiSelectProps {
  locations: Location[];
  selectedLocationIds: string; // comma-separated IDs like "1,2,3"
  onChange: (selectedIds: string) => void;
  disabled?: boolean;
}

const LocationMultiSelect: React.FC<LocationMultiSelectProps> = ({
  locations,
  selectedLocationIds,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedIds = selectedLocationIds
    ? selectedLocationIds.split(",").map((id) => parseInt(id.trim())).filter(id => !isNaN(id))
    : [];

  const filteredLocations = locations.filter((location) =>
    location.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleLocation = (locationId: number) => {
    let newSelectedIds: number[];

    if (selectedIds.includes(locationId)) {
      newSelectedIds = selectedIds.filter((id) => id !== locationId);
    } else {
      newSelectedIds = [...selectedIds, locationId];
    }

    onChange(newSelectedIds.join(","));
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getSelectedLocationNames = (): string => {
    if (selectedIds.length === 0) return "";
    const names = locations
      .filter((loc) => selectedIds.includes(loc.id))
      .map((loc) => loc.label);
    return names.join(", ");
  };

  return (
    <div ref={dropdownRef} className="relative">
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-3 py-1 text-xs border border-gray-300 rounded-sm bg-white cursor-pointer flex items-center justify-between ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <span className={selectedIds.length === 0 ? "text-gray-400" : "text-gray-900"}>
          {selectedIds.length === 0
            ? "Select secondary locations"
            : getSelectedLocationNames()}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="max-h-48 overflow-y-auto">
            {filteredLocations.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500">
                No locations found
              </div>
            ) : (
              filteredLocations.map((location) => (
                <div
                  key={location.id}
                  onClick={() => handleToggleLocation(location.id)}
                  className={`px-3 py-2 text-xs cursor-pointer hover:bg-blue-50 flex items-center gap-2 ${
                    selectedIds.includes(location.id) ? "bg-blue-100" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(location.id)}
                    onChange={() => {}}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span>{location.label}</span>
                </div>
              ))
            )}
          </div>

          {selectedIds.length > 0 && (
            <div className="p-2 border-t border-gray-200 bg-gray-50">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                  setIsOpen(false);
                }}
                className="w-full px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              >
                Clear All ({selectedIds.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationMultiSelect;
